import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Stamp, LogIn, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { useT } from "@/lib/i18n";
import { LOGOS } from "@/lib/brand";
import LanguageSwitcher from "@/components/schengen/LanguageSwitcher";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const [companyName, setCompanyName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error(t("login.errorEmpty"));
      return;
    }
    setBusy(true);
    try {
      const u = await login(username.trim(), password, companyName.trim());
      track("login", { method: "password", role: u?.role || "unknown" });
      toast.success(t("login.success"));
    } catch (err) {
      track("login_failed", { reason: err?.message?.slice(0, 60) || "unknown" });
      toast.error(err.message || t("login.errorFailed"));
    } finally {
      setBusy(false);
    }
  };

  const bgUrl =
    "https://customer-assets.emergentagent.com/job_visa-danomjer/artifacts/077s0wuc_multilevel-elevated-highway-junction-highway-passing-through-modern-city-multiple-directions.webp";

  return (
    <div
      className="relative min-h-screen flex items-center justify-center px-4 py-8 sm:py-10 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url("${bgUrl}")` }}
      data-testid="login-page"
    >
      {/* Dark tint overlay for legibility */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/85 via-black/70 to-[#0A0B10]/90"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 backdrop-blur-[2px]"
      />

      <div className="relative w-full max-w-md">
        {/* App brand */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-11 w-11 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
            <Stamp className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">
              {t("brand.tagline")}
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl leading-none tracking-tight text-white">
              {t("brand.title")}
            </h1>
          </div>
        </div>

        <Card
          className="bg-[#131620] border-[#272A35] rounded-xl p-5 sm:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
          data-testid="login-card"
        >
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="font-serif-display text-2xl text-white tracking-tight">
              {t("login.title")}
            </h2>
            <LanguageSwitcher />
          </div>
          <p className="text-sm text-slate-400 mb-6">{t("login.subtitle")}</p>

          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                {t("login.company")}
              </Label>
              <Input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="bg-[#0C0E14] border-[#272A35] text-slate-100 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] h-11"
                autoComplete="organization"
                placeholder={t("login.companyPlaceholder")}
                data-testid="login-company"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                {t("login.username")}
              </Label>
              <Input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-[#0C0E14] border-[#272A35] text-slate-100 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] h-11"
                autoComplete="username"
                data-testid="login-username"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-400">
                {t("login.password")}
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-[#0C0E14] border-[#272A35] text-slate-100 focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] h-11"
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[#D4AF37] hover:bg-[#FDE047] text-black font-semibold rounded-md h-11"
              data-testid="login-submit"
            >
              <LogIn className="h-4 w-4 mr-1.5" />
              {busy ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          {t("login.noSelfSignup")}
        </p>

        {/* Developer brand - larger + clickable */}
        <div className="mt-12 flex flex-col items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
            {t("login.developedBy")}
          </span>
          <a
            href={LOGOS.developerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center justify-center transition-transform hover:scale-[1.03] active:scale-100"
            aria-label={LOGOS.developerName}
            data-testid="dev-brand-login"
          >
            <img
              src={LOGOS.developer}
              alt={LOGOS.developerName}
              className="h-9 sm:h-11 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.05)] group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.15)] transition-all"
            />
          </a>
        </div>

        <nav
          className="mt-8 flex items-center gap-3 flex-wrap justify-center text-xs text-slate-400"
          aria-label="Legal"
        >
          <a
            href="/impressum"
            className="hover:text-slate-200 transition-colors"
            data-testid="login-imprint-link"
          >
            {t("footer.imprint")}
          </a>
          <span aria-hidden="true">·</span>
          <a
            href="/datenschutz"
            className="hover:text-slate-200 transition-colors"
            data-testid="login-privacy-link"
          >
            {t("footer.privacy")}
          </a>
        </nav>
      </div>
    </div>
  );
}
